import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { AlertCircle, Loader2, MailX } from "lucide-react";
import TrueFlowLogo from "../components/TrueFlowLogo";
import { api } from "../context/AuthContext";

function Marco({ children }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Link to="/">
            <TrueFlowLogo variant="light" width={440} height={120} />
          </Link>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Baja de la secuencia de correos de bienvenida. Se llega desde el enlace
 * "Cancelar suscripción" del pie de esos correos, con `sid` y `token` en la URL
 * (el token es un HMAC de la secuencia; lo verifica el backend).
 *
 * En español a propósito, como el correo del que se viene: es una página
 * pública fuera de la app, no pasa por i18n (misma decisión que el portal del
 * partner). Y pide UN clic de confirmación en vez de darse de baja al cargar:
 * los filtros de correo prefetchean enlaces, y una baja por prefetch daría de
 * baja a gente que no pidió nada.
 */
export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const sid = searchParams.get("sid");
  const token = searchParams.get("token");

  const [estado, setEstado] = useState(sid && token ? "listo" : "sin_datos");
  const [enviando, setEnviando] = useState(false);

  const confirmar = async () => {
    setEnviando(true);
    try {
      await api.post("/unsubscribe", { sid, token }, { skipAuthRedirect: true });
      setEstado("confirmado");
    } catch (e) {
      setEstado(e?.response?.status === 400 ? "invalido" : "error");
    }
    setEnviando(false);
  };

  if (estado === "confirmado") {
    return (
      <Marco>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MailX className="w-5 h-5 text-sky-500" /> Baja confirmada
            </CardTitle>
            <CardDescription>
              No vas a recibir más correos de la secuencia de bienvenida en esta
              casilla. Los correos de tu cuenta (recuperación de contraseña,
              invitaciones) no se ven afectados.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline" className="w-full">
              <Link to="/">Volver a True Flow</Link>
            </Button>
          </CardContent>
        </Card>
      </Marco>
    );
  }

  if (estado === "listo") {
    return (
      <Marco>
        <Card>
          <CardHeader>
            <CardTitle>Cancelar suscripción</CardTitle>
            <CardDescription>
              Vas a dejar de recibir los correos de la secuencia de bienvenida de
              True Flow. ¿Confirmas la baja?
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button onClick={confirmar} disabled={enviando} className="w-full">
              {enviando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Sí, dar de baja esta casilla
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link to="/">No, volver a True Flow</Link>
            </Button>
          </CardContent>
        </Card>
      </Marco>
    );
  }

  return (
    <Marco>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-500" /> Este enlace no sirve
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertDescription>
              {estado === "error"
                ? "No pudimos procesar la baja en este momento. Intenta de nuevo en unos minutos."
                : "El enlace de baja no es válido o está incompleto. Abre el enlace tal como viene en el correo, o escríbenos a contact@trueflow.cash y la procesamos a mano."}
            </AlertDescription>
          </Alert>
          <Button asChild variant="outline" className="w-full">
            <Link to="/">Volver a True Flow</Link>
          </Button>
        </CardContent>
      </Card>
    </Marco>
  );
}
